import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import useScrollReveal from "@/hooks/useScrollReveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = {
  nombre: "",
  empresa: "",
  requerimiento: "",
  email: "",
  telefono: "",
  consentimiento: false,
};

export default function Contact() {
  const [data, setData] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setData((d) => ({ ...d, [k]: value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!data.nombre || data.nombre.trim().length < 2) e.nombre = "Ingresa tu nombre completo.";
    if (!data.empresa || data.empresa.trim().length < 1) e.empresa = "Indica tu empresa.";
    if (!data.requerimiento || data.requerimiento.trim().length < 5)
      e.requerimiento = "Describe brevemente tu requerimiento.";
    if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      e.email = "Ingresa un correo válido.";
    if (!data.telefono || data.telefono.trim().length < 6)
      e.telefono = "Ingresa un teléfono de contacto.";
    if (!data.consentimiento)
      e.consentimiento = "Debes aceptar el Aviso de Privacidad.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Revisa los campos marcados.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...data, consentimiento: !!data.consentimiento };
      const res = await axios.post(`${API}/contact`, payload, { timeout: 20000 });
      if (res.data?.status === "ok") {
        setDone(true);
        setData(INITIAL);
        toast.success("¡Solicitud enviada! Un asesor te contactará pronto.");
      } else {
        toast.error("No pudimos enviar tu solicitud. Intenta de nuevo.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error de conexión. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const formRef = useScrollReveal();
  const sideRef = useScrollReveal();

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="bg-[#FAFAFA] border-t border-[#E5E5E5]"
    >
      <div className="px-6 md:px-12 lg:px-16 max-w-[1440px] mx-auto py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={sideRef} className="reveal-on-scroll lg:col-span-5">
            <span className="eyebrow">[ 06 ] Contacto</span>
            <h2 className="display-2 mt-5 mb-6">
              ¿Listo para escalar tu negocio?
            </h2>
            <p className="text-[#525252] text-base md:text-lg leading-relaxed mb-10 max-w-md">
              Déjanos tus datos y un asesor tecnológico se pondrá en contacto
              contigo en menos de 24 horas hábiles.
            </p>

            <div className="space-y-6 border-t border-[#E5E5E5] pt-8">
              <div>
                <div className="eyebrow mb-2">Tiempo de respuesta</div>
                <div className="font-display text-2xl font-bold">&lt; 24 h</div>
              </div>
              <div>
                <div className="eyebrow mb-2">Cobertura</div>
                <div className="font-display text-2xl font-bold">México · LATAM</div>
              </div>
              <div>
                <div className="eyebrow mb-2">Modelo</div>
                <div className="font-display text-2xl font-bold">B2B · Project & Retainer</div>
              </div>
            </div>
          </div>

          <div ref={formRef} className="reveal-on-scroll lg:col-span-7">
            {done ? (
              <div
                data-testid="contact-success"
                className="bg-white border border-[#E5E5E5] p-10 md:p-14 text-center"
              >
                <div className="w-14 h-14 mx-auto mb-6 bg-[#0047FF]/10 flex items-center justify-center">
                  <CheckCircle2 size={28} className="text-[#0047FF]" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-3xl font-bold tracking-tight mb-3">
                  ¡Solicitud recibida!
                </h3>
                <p className="text-[#525252] max-w-sm mx-auto mb-8">
                  Gracias por escribirnos. Uno de nuestros asesores se pondrá en
                  contacto contigo en menos de 24 horas hábiles.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setDone(false)}
                  className="btn-secondary"
                >
                  Enviar otra solicitud <ArrowRight size={14} />
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="bg-white border border-[#E5E5E5] p-8 md:p-12"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  <div className="md:col-span-1">
                    <label className="eyebrow block mb-2">Nombre completo *</label>
                    <input
                      data-testid="input-nombre"
                      className={`field-input ${errors.nombre ? "has-error" : ""}`}
                      type="text"
                      value={data.nombre}
                      onChange={setField("nombre")}
                      placeholder="Ej. María Hernández"
                      autoComplete="name"
                    />
                    {errors.nombre && (
                      <p className="text-[#FF3B30] text-xs mt-2 font-mono uppercase tracking-wider">
                        {errors.nombre}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-1">
                    <label className="eyebrow block mb-2">Empresa *</label>
                    <input
                      data-testid="input-empresa"
                      className={`field-input ${errors.empresa ? "has-error" : ""}`}
                      type="text"
                      value={data.empresa}
                      onChange={setField("empresa")}
                      placeholder="Nombre de tu compañía"
                      autoComplete="organization"
                    />
                    {errors.empresa && (
                      <p className="text-[#FF3B30] text-xs mt-2 font-mono uppercase tracking-wider">
                        {errors.empresa}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-1">
                    <label className="eyebrow block mb-2">Correo corporativo *</label>
                    <input
                      data-testid="input-email"
                      className={`field-input ${errors.email ? "has-error" : ""}`}
                      type="email"
                      value={data.email}
                      onChange={setField("email")}
                      placeholder="tu@empresa.com"
                      autoComplete="email"
                    />
                    {errors.email && (
                      <p className="text-[#FF3B30] text-xs mt-2 font-mono uppercase tracking-wider">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-1">
                    <label className="eyebrow block mb-2">Teléfono *</label>
                    <input
                      data-testid="input-telefono"
                      className={`field-input ${errors.telefono ? "has-error" : ""}`}
                      type="tel"
                      value={data.telefono}
                      onChange={setField("telefono")}
                      placeholder="+52 55 1234 5678"
                      autoComplete="tel"
                    />
                    {errors.telefono && (
                      <p className="text-[#FF3B30] text-xs mt-2 font-mono uppercase tracking-wider">
                        {errors.telefono}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="eyebrow block mb-2">
                      Requerimiento base *
                    </label>
                    <textarea
                      data-testid="input-requerimiento"
                      className={`field-input ${errors.requerimiento ? "has-error" : ""}`}
                      value={data.requerimiento}
                      onChange={setField("requerimiento")}
                      placeholder="Cuéntanos brevemente qué necesitas desarrollar (objetivo, alcance, timeline aproximado)…"
                      rows={5}
                    />
                    {errors.requerimiento && (
                      <p className="text-[#FF3B30] text-xs mt-2 font-mono uppercase tracking-wider">
                        {errors.requerimiento}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2 mt-2">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        data-testid="input-consentimiento"
                        type="checkbox"
                        checked={data.consentimiento}
                        onChange={setField("consentimiento")}
                        className="mt-1 w-4 h-4 accent-[#0047FF] cursor-pointer"
                      />
                      <span className="text-[13.5px] text-[#525252] leading-relaxed">
                        He leído y acepto el{" "}
                        <Link
                          to="/aviso-de-privacidad"
                          target="_blank"
                          className="underline underline-offset-4 text-[#171717] hover:text-[#0047FF]"
                        >
                          Aviso de Privacidad
                        </Link>
                        . Autorizo el tratamiento de mis datos para fines de contacto
                        comercial.
                      </span>
                    </label>
                    {errors.consentimiento && (
                      <p className="text-[#FF3B30] text-xs mt-2 font-mono uppercase tracking-wider">
                        {errors.consentimiento}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t border-[#E5E5E5] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#A3A3A3]">
                    Tus datos viajan cifrados (HTTPS).
                  </p>
                  <button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Enviando…
                      </>
                    ) : (
                      <>
                        Enviar solicitud <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
